import { Immutable } from "immer";
import React, { useCallback, useState } from "react";
import { t } from "ttag";

import { reverse, templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { submitNPSSurvey } from "../../../api/npsSurvey";
import { RichText } from "../../../components/RichText";

import styles from "./index.module.scss";

type IProps = Immutable<templates.NPSSurveyTemplate>;

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export const NPSSurvey = (props: IProps): React.JSX.Element => {
    const [score, setScore] = useState<number | null>(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = useCallback(async () => {
        setSubmitting(true);
        try {
            await submitNPSSurvey(props.order_id, score, comment);
        } catch (e) {
            console.error("Failed to submit NPS survey", e);
        }
        // Use location.replace so the NPS page is removed from browser
        // history — back from thank-you goes to checkout, which redirects
        // to homepage (matching the existing non-NPS behavior).
        getWindow()?.location.replace(reverse("checkout:thank-you"));
    }, [score, comment, props.order_id]);

    const renderScoreOption = (value: number) => {
        const id = `nps-score-${value}`;
        return (
            <span key={value} className={styles.scoreOption}>
                <input
                    type="radio"
                    id={id}
                    name="nps-score"
                    value={value}
                    checked={score === value}
                    onChange={() => setScore(value)}
                    className={styles.scoreRadio}
                />
                <label
                    htmlFor={id}
                    className={`${styles.scoreButton} ${score === value ? styles.scoreButtonSelected : ""}`}
                >
                    {value}
                </label>
            </span>
        );
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>
                {t`Thank you for your order!`}
                <br />
                {t`Please tell us how we're doing.`}
            </h1>
            <div className={styles.surveyContent}>
                <RichText
                    html={props.survey_question}
                    className={styles.question}
                />
                <div
                    className={styles.scoreSection}
                    role="group"
                    aria-label={t`NPS Score`}
                >
                    <div className={styles.scoreRow}>
                        {SCORES.map(renderScoreOption)}
                    </div>
                    <div className={styles.scoreLabels}>
                        <span>{t`0 = Not At All Likely`}</span>
                        <span>{t`10 = Extremely Likely`}</span>
                    </div>
                </div>
                <div className={styles.commentSection}>
                    <label
                        htmlFor="nps-comment"
                        className={styles.commentLabel}
                    >
                        {t`Share more about your rating. (optional)`}
                    </label>
                    <textarea
                        id="nps-comment"
                        className={styles.commentInput}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        maxLength={2000}
                        rows={5}
                        placeholder={t`Share more about your rating. (optional)`}
                    />
                </div>
                <div className={styles.submitSection}>
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting
                            ? t`Submitting...`
                            : t`View Order Confirmation`}
                    </button>
                </div>
            </div>
        </div>
    );
};
